<template>
    <main class="main">
        <hypo-alert></hypo-alert>
        <mds-loader v-if="loading"></mds-loader>
        <router-view v-else :class="viewClass" class="view"></router-view>
    </main>
</template>

<script>
import "./mbc.css";
import { sync } from "vuex-router-sync";
import { createRouter } from "./router";
import store from "./store";
import MwcVueHelperMixin, { MwcConfigMixin } from "mwc-vue-component-helper";
import { mapState } from "vuex";
import defaultConfig from "./_config";
import * as types from "./store/mutation-types";
import MwcTokenSourceMixin, { DataProviderHelperMixin } from "./mixins/MwcTokenSourceMixin";
import RouteValidator from "./router/guards/routeValidator";
import mergewith from "lodash.mergewith";
import MdsLoader from "@/components/base/Loader.vue";
import HypoAlert from "@/views/HypoAlert";
import { isValidDate } from "@/util";

/* istanbul ignore next */
const router = process.env.VUE_APP_USAGE === "application" ? createRouter("history") : createRouter("abstract");
const validRouteChecker = new RouteValidator({ store });
router.beforeEach((to, from, next) => {
    return validRouteChecker.isValidRoute(to, from) ? next() : next(false);
});
sync(store, router);

function mergeCustomizer(a, b) {
    if (b instanceof Array) {
        return b;
    }
    return undefined;
}

export default {
    name: "hypothetical-illustration",
    store,
    router,
    components: {
        MdsLoader,
        HypoAlert
    },
    mixins: [MwcVueHelperMixin, MwcConfigMixin, MwcTokenSourceMixin, DataProviderHelperMixin],
    props: {
        languageId: {
            type: String,
            default: "en-US"
        }
    },
    computed: {
        ...mapState("preference", {
            preferenceLanguageId: "languageId"
        }),
        viewClass() {
            return {
                "overlay-view": this.$route.path !== "/" && this.$route.path.toLowerCase() !== "/home"
            };
        }
    },
    provide() {
        return {
            locale: this.locale
        };
    },
    data() {
        return {
            loading: false,
            defaultConfig: {},
            locale: {
                language: this.languageId
            }
        };
    },
    created() {
        this.loading = true;
        this.getDefaultPresetConfig().then(presetConfig => {
            this.loading = false;
            this.defaultConfig = {
                intlNamespace: this.mwcId,
                ...presetConfig
            };
            this.$_componentConfig = this.getComponentConfig();
            this.settings = this.$_componentConfig.settings;
            const { languageId, currencyId, adminToolConfig, preference, assetAllocation, gcfc } = this.settings;
            const { format, labels } = this.$_componentConfig;
            const { mwc } = window;
            const FormatterClass = mwc.Formatter;
            this.formatter = new FormatterClass(languageId, format, currencyId);
            const LabellerClass = mwc.Labeller;
            const intlNamespace = this.getIntlNamespace(this.$_componentConfig);
            this.labeller = new LabellerClass(languageId, labels, intlNamespace);
            adminToolConfig.assetAllocation = this.translateAssetClassName(assetAllocation);
            const { presetTimePeriod = [] } = adminToolConfig.localization;
            adminToolConfig.localization.presetTimePeriod = presetTimePeriod.filter(i => {
                const sDate = new Date(i.startDate);
                const eDate = new Date(i.endDate);
                return isValidDate(sDate) && isValidDate(eDate) && sDate <= eDate;
            });
            this.$store.commit(`config/${types.SET_ADMIN_TOOL_CONFIG}`, adminToolConfig);
            this.$store.commit(`config/${types.SET_ASSET_ALLOCATION}`, adminToolConfig.assetAllocation);
            if (gcfc) {
                this.$store.commit(`config/${types.SET_REPORT_DETAILS_CONFIG}`, gcfc);
            }
            this.$store.commit(`labels/${types.SET_LABELS}`, labels);
            this.$store.dispatch(`preference/setPreference`, preference);
            if (this.$router.mode === "abstract") {
                this.$router.replace("/");
            }
        });
    },
    mounted() {
        this.beforeUnloadHandler = e => this.leavingHandler(e);
        window.addEventListener("beforeunload", this.beforeUnloadHandler);
    },
    destroyed() {
        window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    },
    methods: {
        translateAssetClassName(assetGroups) {
            if (assetGroups && assetGroups.length > 0) {
                return assetGroups.map(item => {
                    const compItems = Object.keys(item.compItems).reduce((prev, assetClassId) => {
                        const { labelKey, ...assetClassItem } = item.compItems[assetClassId];
                        const className = this.labeller.getLabel(labelKey);
                        prev[assetClassId] = {
                            ...assetClassItem,
                            className
                        };
                        return prev;
                    }, {});
                    return {
                        ...item,
                        compItems
                    };
                });
            }
            return assetGroups;
        },
        leavingHandler(e) {
            if (this.$store.getters["hypo/hasUnsavedChanges"]) {
                e.preventDefault();
                e.returnValue = "";
            }
        },
        getDefaultPresetConfig() {
            return this.getConfig().then(config => {
                return mergewith(
                    {},
                    defaultConfig,
                    {
                        ...config,
                        labels: {
                            en: config.labels["en-US"],
                            ...config.labels
                        }
                    },
                    mergeCustomizer
                );
            });
        },
        getConfig() {
            return this.$fetch({
                url: `/api/v1/Application/Configs`,
                method: "GET"
            }).then(config => {
                const { general } = config.settings.preference;
                const { language, currency } = general;
                return {
                    ...config,
                    settings: {
                        languageId: language,
                        currency: currency,
                        ...config.settings
                    }
                };
            });
        }
    },
    watch: {
        languageId: {
            handler: function() {
                this.locale.language = this.languageId;
            }
        },
        preferenceLanguageId: {
            handler: function() {
                this.$emit("languageChanged", this.preferenceLanguageId);
            },
            immediate: true
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~@mds/typography";
.main {
    font-family: $mds-typography-font-family-sans-serif;
    font-weight: $mds-typography-font-weight-light;
    width: 100%;
    height: 100%;
    .view {
        height: 100%;
        overflow: auto;
    }
}
</style>
